import {
  Dropdown,
  PaginatedDropdownList,
  ProductsCategoriesListListData,
  productsCategoriesListListInfiniteOptions,
  productsCreateMutation,
  productsDestroyMutation,
  productsListOptions,
  productsListQueryKey,
  productsPartialUpdateMutation,
  productsRetrieveOptions,
  ReturnProductReadable
} from '@crm/api'
import { ConfirmModal, InfiniteSelect } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclatureProductsCatalog,
  StatusSelect
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { composePrice, defaultNonPagination } from '@crm/utils'
import { ConditionalText, DayLabel, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useQueryUpdate,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Tag } from 'antd'
import { Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'products'

export const NomenclaturesProductsCatalog = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const sorter = useTableSorter()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const { resetPagination } = useResetPagination()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: products, isLoading } = useQuery(
    productsListOptions({ query: { ...defaultNonPagination, ...params } })
  )

  const { mutate } = useMutation({
    ...productsDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([productsListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnProductReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.sku'),
        dataIndex: 'sku',
        key: 'sku',
        sorter: true,
        render: (sku) => (
          <ConditionalText condition={!!sku}>{sku}</ConditionalText>
        )
      },
      {
        title: t('nomenclature.category'),
        dataIndex: 'category',
        key: 'category',
        sorter: true,
        render: (category) => (
          <ConditionalText condition={!!category?.name}>
            {category?.name}
          </ConditionalText>
        )
      },
      {
        title: t('nomenclature.price'),
        dataIndex: 'price',
        key: 'price',
        sorter: true,
        render: (price, record) => (
          <ConditionalText condition={!!price}>
            {composePrice(price, record?.currency?.symbol)}
          </ConditionalText>
        )
      },
      {
        title: t('opportunity.unit_of_measure'),
        dataIndex: 'uom',
        key: 'uom',
        sorter: true,
        render: (uom) => (
          <ConditionalText condition={!!uom?.name}>{uom?.name}</ConditionalText>
        )
      },
      {
        title: t('nomenclature.status'),
        dataIndex: 'is_active',
        key: 'is_active',
        render: (is_active: string) => (
          <Tag color={is_active ? 'green' : 'red'}>
            {is_active ? t('nomenclature.active') : t('nomenclature.inactive')}
          </Tag>
        )
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },

      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <CatalogActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const productsFilters = (
    <>
      <InfiniteSelect<
        ProductsCategoriesListListData,
        PaginatedDropdownList,
        Dropdown
      >
        value={params.category ? Number(params.category) : undefined}
        className="select-min-width"
        popupMatchSelectWidth
        placeholder={t('nomenclature.category')}
        getQueryFunction={productsCategoriesListListInfiniteOptions}
        onChange={(category) => updateQuery({ category })}
        baseOptions={{
          query: {
            preference: params?.category
          }
        }}
      />

      <StatusSelect
        value={params.is_active}
        onChange={(is_active) =>
          updateQuery({ is_active, page: defaultFilters.page })
        }
        placeholder={t('nomenclature.status')}
        popupMatchSelectWidth={120}
      />
    </>
  )

  return (
    <>
      <TableContainer
        data={products}
        columns={columns}
        loading={isLoading}
        leftChildren={productsFilters}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_product')}
          </CatalogAddButton>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={productsRetrieveOptions}
        onCreate={productsCreateMutation}
        onUpdate={productsPartialUpdateMutation}
        onCloseModal={onCloseModal}
        width={800}
      >
        <FormNomenclatureProductsCatalog />
      </FormModal>
    </>
  )
}
